import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Textarea } from '@/components/elements/textarea'
import { SlidePanel } from '@/components/SlidePanel'
import { addEntityJob, updateEntityJob } from '@/services/entity.service'
import { addOrganizationJob, updateOrganizationJob } from '@/services/organization.service'
import { toast } from '@/utils/helpers'

const DEFAULT_VALUES = {
  title: '',
  description: '',
}

// Reused for both facility jobs and org-shared jobs (RSCX-320): pass
// organizationId for the org-scoped variant.
const AddEditJobSlideout = ({ editJob, entityId, organizationId, queryKey, resetAndClose }) => {
  const isOrgScoped = Boolean(organizationId)
  const defaultValues = useMemo(() => {
    if (!editJob) return DEFAULT_VALUES

    return {
      ...DEFAULT_VALUES,
      id: editJob.id,
      title: editJob.title || '',
      description: editJob.description || '',
    }
  }, [editJob])

  const form = useForm({ defaultValues })

  const resetForm = () => {
    form.reset(DEFAULT_VALUES)
    resetAndClose()
  }

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending, mutate: mutateJob } = useMutation({
    mutationFn: (data) => {
      if (isOrgScoped) {
        return editJob
          ? updateOrganizationJob({ organizationId, ...data })
          : addOrganizationJob({ organizationId, ...data })
      }
      return editJob ? updateEntityJob({ entityId, ...data }) : addEntityJob({ entityId, ...data })
    },
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(
        editJob ? `${data.title} updated successfully.` : `${data.title} added successfully.`,
      )
      resetForm()

      // Refresh jobs list
      queryClient.invalidateQueries({ queryKey })
    },
  })

  /**
   * Handles form submission
   * @param {object} data
   */
  const onSubmit = async (data) => mutateJob({ ...data })

  return (
    <SlidePanel
      actions={[
        {
          label: 'Cancel',
          onClick: () => resetAndClose(),
          type: 'cancel',
        },
        {
          label: `${editJob ? 'Update' : 'Add'} Job`,
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-4">
              <span className="leading-6 font-bold">Job Details</span>
              <FormField
                control={form.control}
                disabled={isPending}
                name="id"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input id="id" type="hidden" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                disabled={isPending}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input id="title" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              <FormField
                control={form.control}
                disabled={isPending}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea id="description" rows={4} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
      }
      loading={isPending}
      title={`${editJob ? 'Update' : 'Add'} Job`}
      setOpen={resetForm}
    />
  )
}

AddEditJobSlideout.propTypes = {
  editJob: PropTypes.object,
  entityId: PropTypes.string,
  organizationId: PropTypes.string,
  queryKey: PropTypes.array.isRequired,
  resetAndClose: PropTypes.func.isRequired,
}

export default AddEditJobSlideout
